import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { Toast, ToastVariant, ToastAction } from './Toast';
import { cn } from '@/lib/utils/index';

// ============================================
// TYPES
// ============================================
interface ToastData {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: ToastData[];
  toast: (options: ToastOptions) => string;
  success: (title: string, options?: Partial<ToastOptions>) => string;
  warning: (title: string, options?: Partial<ToastOptions>) => string;
  error: (title: string, options?: Partial<ToastOptions>) => string;
  info: (title: string, options?: Partial<ToastOptions>) => string;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  action?: ToastAction;
}

// ============================================
// CONTEXT
// ============================================
const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// ============================================
// HOOK
// ============================================
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// ============================================
// PROVIDER
// ============================================
interface ToastProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
  defaultDuration?: number;
}

export function ToastProvider({
  children,
  maxToasts = 3,
  defaultDuration = 4000,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Generate unique ID
  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Dismiss a toast
  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Dismiss all toasts
  const dismissAll = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  // Add a toast
  const toast = useCallback(
    (options: ToastOptions): string => {
      const id = generateId();
      const duration = options.duration ?? defaultDuration;
      const variant = options.variant ?? 'info';

      const newToast: ToastData = {
        id,
        title: options.title,
        description: options.description,
        variant,
        duration,
        action: options.action,
      };

      setToasts((prev) => {
        const updated = [...prev, newToast];
        // Keep only maxToasts
        if (updated.length > maxToasts) {
          const removed = updated.shift();
          if (removed) {
            const timer = timersRef.current.get(removed.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(removed.id);
            }
          }
        }
        return updated;
      });

      // Auto-dismiss if duration > 0
      if (duration > 0) {
        const timer = setTimeout(() => {
          dismiss(id);
        }, duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [generateId, defaultDuration, maxToasts, dismiss]
  );

  // Convenience methods
  const success = useCallback(
    (title: string, options?: Partial<ToastOptions>) => {
      return toast({ ...options, title, variant: 'success' });
    },
    [toast]
  );

  const warning = useCallback(
    (title: string, options?: Partial<ToastOptions>) => {
      return toast({ ...options, title, variant: 'warning' });
    },
    [toast]
  );

  const error = useCallback(
    (title: string, options?: Partial<ToastOptions>) => {
      return toast({ ...options, title, variant: 'error' });
    },
    [toast]
  );

  const info = useCallback(
    (title: string, options?: Partial<ToastOptions>) => {
      return toast({ ...options, title, variant: 'info' });
    },
    [toast]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const value: ToastContextValue = {
    toasts,
    toast,
    success,
    warning,
    error,
    info,
    dismiss,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container */}
      <div
        className={cn(
          'fixed z-50 pointer-events-none',
          // Desktop: bottom-right
          'bottom-4 right-4',
          // Mobile: bottom-center
          'sm:bottom-4 sm:right-4',
          'max-sm:left-4 max-sm:right-4 max-sm:bottom-4'
        )}
      >
        <div className="flex flex-col gap-2 items-end max-sm:items-stretch">
          {toasts.map((t) => (
            <div key={t.id} className="pointer-events-auto animate-fade-in-up">
              <Toast
                id={t.id}
                title={t.title}
                description={t.description}
                variant={t.variant}
                duration={t.duration}
                action={t.action}
                onClose={() => dismiss(t.id)}
              />
            </div>
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

export { ToastContext };
